import React from 'react';
import { List, useTable, DeleteButton, EditButton, ShowButton, CreateButton, useModalForm } from '@refinedev/antd';
import { Table, Space, Typography, Tag, Button, Modal, Form, Input, Checkbox, Select, Tooltip, Card } from 'antd';
import { CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined, EditOutlined, DeleteOutlined, EyeOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { CreateArrivalDeptRule } from './create';
import { EditArrivalDeptRule } from './edit';

const { Text, Title } = Typography;
const { Option } = Select;

const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

interface ArrivalDepartureRule {
  id: number;
  label: string;
  arrival_days: number[];
  departure_days: number[];
  valid_from_month: number;
  valid_to_month: number;
  recurring: boolean;
  is_global: boolean;
  created_at: string;
  updated_at: string;
}

export const ArrivalDepartureList: React.FC = () => {
  const { tableProps } = useTable<ArrivalDepartureRule>({
    resource: 'arrival-departure-rules',
    syncWithLocation: false,
  });

  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: showCreateModal,
  } = useModalForm({
    action: 'create',
    resource: 'arrival-departure-rules',
  });

  const {
    modalProps: editModalProps,
    formProps: editFormProps,
    show: showEditModal,
  } = useModalForm({
    action: 'edit',
    resource: 'arrival-departure-rules',
  });

  const formatDays = (days: number[]) => {
    if (!Array.isArray(days) || days.length === 0) return '-';
    return days.map(day => dayNames[day] || '?').join(', ');
  };

  const formatValidityPeriod = (rule: ArrivalDepartureRule) => {
    if (rule.is_global) {
      return <Tag color="green">Ganzjährig</Tag>;
    }
    
    const fromMonth = monthNames[rule.valid_from_month - 1] || '?';
    const toMonth = monthNames[rule.valid_to_month - 1] || '?';
    
    return (
      <div>
        <Tag color="blue">{fromMonth} - {toMonth}</Tag>
        {rule.recurring && <Tag color="orange">Jährlich</Tag>}
      </div>
    );
  };

  const getDescription = (rule: ArrivalDepartureRule) => {
    const arrivalText = formatDays(rule.arrival_days);
    const departureText = formatDays(rule.departure_days);
    
    return `Anreise: ${arrivalText} | Abreise: ${departureText}`;
  };

  const columns = [
    {
      title: 'Regel',
      dataIndex: 'label',
      key: 'label',
      render: (text: string, record: ArrivalDepartureRule) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {getDescription(record)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Anreisetage',
      dataIndex: 'arrival_days',
      key: 'arrival_days',
      render: (days: number[]) => (
        <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
          {dayNames.map((dayName, index) => (
            <Tag 
              key={index} 
              color={days.includes(index) ? 'green' : 'default'}
              style={{ 
                margin: '1px',
                fontSize: '10px',
                padding: '0 4px',
                opacity: days.includes(index) ? 1 : 0.3
              }}
            >
              {dayName}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Abreisetage',
      dataIndex: 'departure_days',
      key: 'departure_days',
      render: (days: number[]) => (
        <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
          {dayNames.map((dayName, index) => (
            <Tag 
              key={index} 
              color={days.includes(index) ? 'blue' : 'default'}
              style={{ 
                margin: '1px',
                fontSize: '10px',
                padding: '0 4px',
                opacity: days.includes(index) ? 1 : 0.3
              }}
            >
              {dayName}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Gültigkeit',
      key: 'validity',
      render: (record: ArrivalDepartureRule) => formatValidityPeriod(record),
    },
    {
      title: 'Erstellt',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('de-DE'),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (record: ArrivalDepartureRule) => (
        <Space>
          <Tooltip title="Bearbeiten">
            <EditButton
              hideText
              size="small"
              recordItemId={record.id}
              onClick={() => showEditModal(record.id)}
            />
          </Tooltip>
          <Tooltip title="Löschen">
            <DeleteButton
              hideText
              size="small"
              recordItemId={record.id}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        headerButtons={
          <CreateButton
            onClick={() => showCreateModal()}
            icon={<PlusOutlined />}
          >
            Neue Regel erstellen
          </CreateButton>
        }
      >
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Title level={4}>
              <CalendarOutlined /> An- & Abreise-Regeln
            </Title>
            <Text type="secondary">
              Verwalten Sie die erlaubten An- und Abreisetage für Ihre Unterkünfte. 
              Diese Regeln können später den einzelnen Objekten zugewiesen werden.
            </Text>
          </div>
          
          <Table 
            {...tableProps} 
            columns={columns}
            rowKey="id"
            pagination={{
              ...tableProps.pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} von ${total} Regeln`,
            }}
          />
        </Card>
      </List>

      {/* Create Modal */}
      <Modal
        {...createModalProps}
        width={800}
        title="Neue An-/Abreise-Regel erstellen"
      >
        {createFormProps && <CreateArrivalDeptRule formProps={createFormProps} />}
      </Modal>

      {/* Edit Modal */}
      <Modal
        {...editModalProps}
        width={800}
        title="An-/Abreise-Regel bearbeiten"
      >
        {editFormProps && <EditArrivalDeptRule formProps={editFormProps} />}
      </Modal>
    </>
  );
}; 